import { TemplateRef } from "@angular/core";
import { FormControl } from "@angular/forms";
import { HasValue } from 'ng/src/app/core/value';
import { SelectorItem } from "ng/src/app/shared/item-selectors/item-selector.model";
import { PersistentDialogSettings } from "ng/src/app/shared/persistent-dialog/persistent-dialog.model";

export interface FormInputDialogEntryMap {
	[Key: string]: FormInputDialogEntry<any>
}

export interface CompleteButtonOptions {
	disabled?: boolean,
	label?: string,
	class?: string | string[] | Record<string, boolean>,
}

export type MapTypeCallback<MapType extends FormInputDialogEntryMap> = {[K in keyof MapType]: MapType[K]['formControl']}

export class FormInputDialogForm<MapType extends FormInputDialogEntryMap = any> {
	private _onComplete?: (val: MapTypeCallback<MapType>) => void;
	private _onClose?: () => void;
	private _header?: string;
	private _escapeCloses?: boolean;
	private _position: PersistentDialogSettings['position'];
	private _completeButtonOptions?: CompleteButtonOptions;
	
	get onComplete() {
		return this._onComplete;
	}
	
	get onClose() {
		return this._onClose;
	}
	
	get header() {
		return this._header;
	}
	
	get escapeCloses() {
		return this._escapeCloses;
	}
	
	get position() {
		return this._position;
	}
	
	get completeButtonOptions() {
		return this._completeButtonOptions;
	}
	
	constructor(
		readonly map: MapType
	) {}
	
	getControlMap(): MapTypeCallback<MapType> {
		return Object.entries(this.map).reduce((map, current) => {
			map[current[0] as keyof MapType] = current[1].formControl;
			return map;
		}, {} as Record<keyof MapType, FormControl>);
	}
	
	setOnComplete(val: FormInputDialogForm<MapType>['_onComplete']) {
		this._onComplete = val;
		return this;
	}
	
	setOnClose(val: FormInputDialogForm<MapType>['_onClose']) {
		this._onClose = val;
		return this;
	}
	
	setHeader(val: FormInputDialogForm<MapType>['_header']) {
		this._header = val;
		return this;
	}
	
	setEscapeCloses(val: FormInputDialogForm<MapType>['_escapeCloses']) {
		this._escapeCloses = val;
		return this;
	}
	
	setPosition(val: FormInputDialogForm<MapType>['_position']) {
		this._position = val;
		return this;
	}
	
	setCompleteButtonOptions(val: FormInputDialogForm<MapType>['_completeButtonOptions']) {
		this._completeButtonOptions = val;
		return this;
	}
	
}

export abstract class FormInputDialogEntry<T> {
	private _label?: string;
	private _visible?: boolean | HasValue<boolean>;
	
	private _cachedVisible?: boolean;
	
	get label() {
		return this._label;
	}
	
	get visible() {
		const visible = typeof this._visible === 'boolean' ? this._visible : this._visible?.value ?? true;
		if (this._cachedVisible != null && this._cachedVisible !== visible) {
			this.formControl.updateValueAndValidity();
		}
		
		this._cachedVisible = visible;
		return visible;
	}
	
	protected constructor(
		public readonly formControl: FormControl<T | null>
	) {}
	
	setLabel(value: FormInputDialogEntry<T>['label']) {
		this._label = value;
		return this;
	}
	
	setVisible(value: boolean | HasValue<boolean>) {
		this._visible = value;
		return this;
	}
}

export abstract class InputTypeDialogEntry<T> extends FormInputDialogEntry<T> implements HasPlaceHolder {
	get placeholder() {
		return this._placeholder;
	}
	
	private _placeholder?: string;
	
	setPlaceholder(val: InputTypeDialogEntry<T>['placeholder']) {
		this._placeholder = val;
		return this;
	}
}

export class BooleanInputDialogEntry extends FormInputDialogEntry<boolean> {
	
	constructor(
		control: FormControl<boolean | null>
	) {
		super(control);
	}
}

export class StringInputDialogEntry extends InputTypeDialogEntry<string> {
	
	constructor(
		control: FormControl<string | null>
	) {
		super(control);
	}
}

export class NumberInputDialogEntry extends InputTypeDialogEntry<number> {
	
	constructor(
		control: FormControl<number | null>
	) {
		super(control);
	}
}

export class SelectorInputDialogEntry<T> extends FormInputDialogEntry<T> implements HasPlaceHolder {
	get placeholder() {
		return this._placeholder;
	}
	
	private _placeholder?: string
	
	constructor(
		control: FormControl<T | null>,
		readonly options: SelectorItem<T>[]
	) {
		super(control);
	}
	
	setPlaceholder(val: SelectorInputDialogEntry<T>['placeholder']) {
		this._placeholder = val;
		return this;
	}
}

export class TemplateInputDialogEntry<T, OptionType = any> extends FormInputDialogEntry<T> {
	constructor(
		control: FormControl<T | null>,
		readonly templateRef: TemplateRef<FormControl<T>>,
		readonly templateOptions?: OptionType)
	{
		super(control);
	}
}

export interface HasPlaceHolder {
	placeholder?: string
}